import { useEffect, useRef, useState, forwardRef } from "react";
import { Link } from "react-router-dom";
import HTMLFlipBook from "react-pageflip";
import { ArrowLeft, ChevronLeft, ChevronRight, Plus, BookOpen, Recycle, Leaf, Sprout, TreePine } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

const Page = forwardRef(({ children, className = "" }, ref) => (
  <div ref={ref} className={`bg-[#FDFBF7] overflow-hidden ${className}`} style={{ boxShadow: "inset -8px 0 24px -18px rgba(0,0,0,0.4)" }}>
    {children}
  </div>
));

const CHAPTER_ORDER = ["Primi Piatti Pronti", "Secondi Piatti Pronti", "Contorni", "Fritti", "Kris", "Dolci", "Sughi Pronti", "Gluten Fit"];

const ChapterPage = forwardRef(({ category, index, count, image }, ref) => (
  <div ref={ref} className="bg-[#2E8A57] overflow-hidden">
    <div className="h-full flex flex-col items-center justify-center text-center p-8 border-[5px] border-[#FDFBF7]/20 m-3 rounded-lg" style={{ height: "calc(100% - 24px)" }}>
      <p className="font-mono-accent text-[10px] uppercase tracking-[0.35em] text-[#7DC242]">Capitolo {String(index).padStart(2, "0")}</p>
      <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-[#FDFBF7]/30 my-6">
        <img src={image} alt={category} className="w-full h-full object-cover" draggable={false} />
      </div>
      <h2 className="font-serif-display font-bold text-2xl text-[#FDFBF7] leading-tight">{category}</h2>
      <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#FDFBF7]/70 mt-4">{count} piatti</p>
    </div>
  </div>
));

const ProductPage = forwardRef(({ product, pageNum, onAdd }, ref) => (
  <div ref={ref} className="bg-[#FDFBF7] overflow-hidden" style={{ boxShadow: "inset -8px 0 24px -18px rgba(0,0,0,0.35)" }}>
    <div className="h-full flex flex-col p-7">
      <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#2E8A57] mb-3">{product.category}</p>
      <div className="rounded-xl overflow-hidden border border-[#E7E0D8] aspect-[4/3] mb-4">
        <img src={product.image} alt={product.name} className="w-full h-full object-cover" draggable={false} />
      </div>
      <h3 className="font-serif-display font-bold text-xl leading-snug text-[#1C1917]">{product.name}</h3>
      {(product.weight || product.shelf_life) && (
        <p className="mt-1.5 font-mono-accent text-[10px] uppercase tracking-wide text-[#3C6E53]">
          {product.weight}{product.weight && product.shelf_life ? " · " : ""}{product.shelf_life}
        </p>
      )}
      {product.ingredients && (
        <p className="mt-2 text-xs text-[#57534E] leading-relaxed line-clamp-5">
          <span className="font-semibold text-[#2E8A57]">Ingredienti: </span>
          {product.ingredients.replace(/\[|\]/g, "")}
        </p>
      )}
      <div className="mt-auto pt-3 flex items-center justify-between">
        <span className="font-mono-accent font-semibold text-[#2E8A57] text-xl">{product.price.toFixed(2).replace(".", ",")} €</span>
        <button
          data-testid={`book-add-${product.slug}`}
          onClick={() => onAdd(product)}
          className="btn-tuscan inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold"
        >
          <Plus className="w-4 h-4" /> Aggiungi
        </button>
      </div>
      <p className="mt-3 text-center font-mono-accent text-[9px] text-[#B4ADA3]">— {pageNum} —</p>
    </div>
  </div>
));

export default function FlipBookCatalog() {
  const [products, setProducts] = useState([]);
  const bookRef = useRef(null);
  const { addItem } = useCart();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const onAdd = (p) => {
    addItem(p);
    toast.success(`${p.name} aggiunto al carrello`);
  };

  const chapters = CHAPTER_ORDER.map((cat) => ({
    cat,
    items: products.filter((p) => p.category === cat),
  })).filter((c) => c.items.length > 0);

  let pageNum = 0;
  const pages = [];
  chapters.forEach((c, ci) => {
    pages.push(<ChapterPage key={`ch-${c.cat}`} category={c.cat} index={ci + 1} count={c.items.length} image={c.items[0].image} />);
    c.items.forEach((p) => {
      pageNum += 1;
      pages.push(<ProductPage key={p.slug} product={p} pageNum={pageNum} onAdd={onAdd} />);
    });
  });

  return (
    <div data-testid="flipbook-page" className="min-h-screen bg-[#243329] py-10 lg:py-14">
      <div className="max-w-6xl mx-auto px-4 flex items-center justify-between mb-8">
        <Link to="/prodotti" data-testid="book-back-link" className="inline-flex items-center gap-2 text-sm text-[#FDFBF7]/70 hover:text-[#FDFBF7] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Torna al catalogo
        </Link>
        <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#7DC242] hidden sm:flex items-center gap-2">
          <BookOpen className="w-4 h-4" /> Sfoglia trascinando gli angoli
        </p>
      </div>

      {products.length > 0 && (
        <div className="flex justify-center px-2 mx-auto w-full max-w-[1160px]">
          <HTMLFlipBook
            ref={bookRef}
            width={480}
            height={660}
            size="stretch"
            minWidth={300}
            maxWidth={560}
            minHeight={440}
            maxHeight={760}
            showCover={true}
            maxShadowOpacity={0.5}
            mobileScrollSupport={true}
            className="flipbook-shadow"
          >
            <Page className="!bg-[#2E8A57]">
              <div className="h-full flex flex-col items-center justify-center text-center p-8 border-[6px] border-[#FDFBF7]/20 m-3 rounded-lg" style={{ height: "calc(100% - 24px)" }}>
                <img src="/logo-icon.png" alt="" className="w-28 h-auto mb-6" draggable={false} />
                <h1 className="font-serif-display font-bold text-3xl text-[#FDFBF7] leading-tight">Il Menu</h1>
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#FDFBF7]/80 mt-3">Piatti Pronti Gluten Free</p>
                <p className="mt-8 text-xs text-[#FDFBF7]/70">{products.length} piatti · {chapters.length} capitoli · 100% italiani</p>
              </div>
            </Page>
            <Page>
              <div data-testid="book-packaging-page" className="h-full flex flex-col p-7">
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#2E8A57] mb-3">Il nostro impegno</p>
                <div className="rounded-xl overflow-hidden border border-[#E7E0D8] aspect-[4/3] mb-4">
                  <img src="/packaging-3.jpg" alt="Packaging sostenibile in carta kraft" className="w-full h-full object-cover" draggable={false} />
                </div>
                <h3 className="font-serif-display font-bold text-xl leading-snug text-[#1C1917]">Packaging 100% sostenibile</h3>
                <p className="mt-2 text-xs text-[#57534E] leading-relaxed">
                  Ogni piatto di questo menu arriva in una scatola di carta kraft naturale con vassoio in cellulosa, pronto per microonde e forno.
                </p>
                <div className="mt-3 space-y-2">
                  {[
                    { icon: Recycle, t: "100% riciclabile nella carta" },
                    { icon: Leaf, t: "Compostabile e biodegradabile" },
                    { icon: Sprout, t: "-80% plastica vs vaschette classiche" },
                    { icon: TreePine, t: "Carta FSC® da foreste certificate" },
                  ].map((b) => (
                    <div key={b.t} className="flex items-center gap-2">
                      <b.icon className="w-3.5 h-3.5 text-[#2E8A57] shrink-0" />
                      <p className="text-xs font-medium text-[#1C1917]">{b.t}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-auto pt-3 text-center font-mono-accent text-[9px] uppercase tracking-[0.2em] text-[#3C6E53]">
                  Buono per te, buono per il pianeta
                </p>
              </div>
            </Page>
            {pages}
            <Page className="!bg-[#2E8A57]">
              <div className="h-full flex flex-col items-center justify-center text-center p-8">
                <p className="font-serif-display italic text-2xl text-[#FDFBF7]">Buon appetito!</p>
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#FDFBF7]/70 mt-4">piattiprontiglutenfree.com</p>
              </div>
            </Page>
          </HTMLFlipBook>
        </div>
      )}

      <div className="flex justify-center gap-4 mt-8">
        <button
          data-testid="book-prev-btn"
          onClick={() => bookRef.current?.pageFlip()?.flipPrev()}
          className="w-12 h-12 rounded-full border border-[#FDFBF7]/30 text-[#FDFBF7] flex items-center justify-center hover:bg-[#FDFBF7]/10 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          data-testid="book-next-btn"
          onClick={() => bookRef.current?.pageFlip()?.flipNext()}
          className="w-12 h-12 rounded-full border border-[#FDFBF7]/30 text-[#FDFBF7] flex items-center justify-center hover:bg-[#FDFBF7]/10 transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
